import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { Observable, of } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Evaluation } from 'src/app/shared/models/app.models';
import { UserService } from 'src/app/shared/services/user.service';

@Component({
  selector: 'app-evaluations',
  templateUrl: './evaluations.component.html',
  styleUrls: ['./evaluations.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EvaluationsComponent implements OnInit {
  AppConstants = AppConstants;
  evaluations: Evaluation[] = [];
  tableColumns: TableColumn[] = [];
  
  // Using inject() for better tree-shaking
  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private _router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private userService = inject(UserService);
  private readonly EVALUATOR_ROLE = 'Evaluator';

  ngOnInit(): void {
    this.initializeTableColumns();
    this.fetchEvaluations();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View Details'
      },
      {
        iconPath: 'assets/icons/publish-evaluation-icon.svg',
        action: 'scoring',
        title: 'Evaluation Scoring',
        // Only show scoring button for Evaluators (assigned users).
        // Secretaries and other roles will not see this action.
        condition: (row: Evaluation) => this.canSeeScoring(row)
      }
    ];

    this.tableColumns = [
      {
        field: 'evaluationId',
        header: 'Evaluation ID',
        type: 'text'
      },
      {
        field: 'prNumber',
        header: 'PR',
        type: 'text'
      },
      {
        field: 'evaluationType',
        header: 'Evaluation Type',
        type: 'badge',
        badgeClass: 'evaluation-type-badge'
      },
      {
        field: 'assignedToEn',
        header: 'Assigned To',
        type: 'text'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'comments',
        header: 'Comments',
        type: 'text'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        sortable: false,
        actions: actionIcons
      }
    ];
  }

  fetchEvaluations(): void {
    this._loaderService.showLoader();
    
    // Set base URL for API service
    this.apiService.setBaseUrl(environment.API_URL);
    
    // Call API to get all evaluations with RxJS operators
    this.apiService.get(
      AppConstants.CONTROLLERS.EVALUATIONS,
      AppConstants.ACTIONS.GetAllEvaluations,
      true,
      environment.APIs_PREFIX
    ).pipe(
      map((response: any) => {
        // Extract data from response object
        if (response?.data && Array.isArray(response.data)) {
          return this.mapEvaluationsResponse(response.data);
        } else if (Array.isArray(response)) {
          return this.mapEvaluationsResponse(response);
        }
        return [];
      }),
      catchError((error) => {
        console.error('Failed to fetch evaluations:', error);
        return of([]);
      })
    ).subscribe({
      next: (evaluations) => {
        this.evaluations = evaluations;
        this.cdr.markForCheck();
        this._loaderService.hideLoader();
      },
      error: (error) => {
        console.error('Error fetching evaluations:', error);
        this.evaluations = [];
        this.cdr.markForCheck();
        this._loaderService.hideLoader();
      }
    });
  }

  // Only Secretary can see/use Assign Evaluations
  isSecretary(): boolean {
    let userRole: any = this.userService.user?.Role;

    // Fallback: try to read role from token if not set on user
    if (!userRole && this.userService.token) {
      try {
        const token = this.userService.token;
        const payload = JSON.parse(atob(token.split('.')[1]));
        userRole = payload?.['Role'] || payload?.['role'] || payload?.[AppConstants.CLAIMS.Role];
      } catch (e) {
        console.warn('Could not decode token for role in EvaluationsComponent:', e);
      }
    }

    const roleValue = typeof userRole === 'string' && !isNaN(Number(userRole)) ? Number(userRole) : userRole;
    const isSecretary =
      userRole === 'Secretary' ||
      userRole === 'secretary' ||
      userRole === 'SECRETARY' ||
      userRole === 'Tender Committee Secretary' ||
      roleValue === 4;

    return isSecretary;
  }

  // Evaluator role check (matches nav-bar logic)
  private isEvaluator(): boolean {
    const role = (this.userService.user?.Role || '').toString().trim();
    return role === this.EVALUATOR_ROLE || role.toUpperCase() === this.EVALUATOR_ROLE.toUpperCase();
  }

  // Only evaluators (assigned users) should see scoring action.
  // Backend already filters evaluations so non-secretaries only see their own evaluations.
  private canSeeScoring(row: Evaluation): boolean {
    return this.isEvaluator();
  }

  /**
   * Maps API response data to Evaluation interface
   * Converts tenderId from number to string using tender.tenderID
   */
  private mapEvaluationsResponse(data: any[]): Evaluation[] {
    return data.map((item: any) => {
      const assignedEvaluators = item.assignedEvaluators || item.AssignedEvaluators || [];
      const selectedIds = assignedEvaluators.length
        ? assignedEvaluators.map((a: any) => String(a.userId ?? a.UserId ?? '')).filter(Boolean)
        : (item.selectedEvaluatorIds || []);
      return {
        id: item.id || 0,
        evaluationId: item.evaluationId || '',
        prNumber: item.prNumber || item.PrNumber || '',
        tenderId: item.tenderID || item.tenderId?.toString() || '',
        evaluationType: item.evaluationType || '',
        evaluatorId: item.evaluatorId || 0,
        assignedToEn: item.assignedToEn || '',
        assignedToAr: item.assignedToAr || '',
        status: item.status ?? '',
        score: item.score ?? null,
        comments: item.comments || null,
        tender: item.tender || null,
        evaluator: item.evaluator || null,
        bidId: item.bidId,
        criteria: item.criteria,
        selectedEvaluatorIds: selectedIds,
        dueDate: item.dueDate ?? item.DueDate ?? null,
        assignedEvaluators: assignedEvaluators.length ? assignedEvaluators : undefined
      };
    });
  }
  
  // TrackBy function for better *ngFor performance
  trackByEvaluationId(index: number, item: Evaluation): string {
    return item.evaluationId || index.toString();
  }

  uploadEvaluations(): void {
    // TODO: Implement upload functionality
    console.log('Upload Evaluations');
  }

  onTableActionClick(event: {action: string, row: any}): void {
    switch(event.action) {
      case 'view':
        if (this.isDraftEvaluation(event.row) && this.isSecretary()) {
          this.viewDraftEvaluation(event.row);
        } else {
          this.viewEvaluationDetails(event.row.evaluationId);
        }
        break;
      case 'scoring':
        this.viewEvaluationScoring(event.row.evaluationId);
        break;
      case 'assign':
        this.assignEvaluation(event.row.evaluationId);
        break;
      case 'publish':
        this.publishMeeting(event.row.evaluationId);
        break;
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'tenderId') {
      this.viewTender(event.row.tenderId);
    }
  }

  /** True when the evaluation is in Draft status (view should go to assign-evaluators with draft data). */
  private isDraftEvaluation(row: any): boolean {
    const s = row?.status;
    return s === 'Draft' || s === 'draft' || s === 0;
  }

  /** Navigate to Assign Evaluators with only evaluation ID; page will fetch draft details from API. */
  viewDraftEvaluation(row: any): void {
    const evaluationId = row?.evaluationId || row?.evaluationID || '';
    if (!evaluationId) return;
    this._router.navigate(
      [`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.ASSIGN_EVALUATORS}`],
      { queryParams: { evaluationId } }
    );
  }

  viewEvaluationDetails(evaluationId: string): void {
    // Navigate to Evaluation Details page
    this._router.navigate(
      [`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.EVALUATION_DETAILS}/${evaluationId}`]
    );
  }

  viewEvaluationScoring(evaluationId: string): void {
    const evaluation = this.evaluations.find(e => e.evaluationId === evaluationId);
    
    if (!evaluation) {
      console.error('Evaluation not found:', evaluationId);
      return;
    }
    
    const queryParams: any = {
      evaluationId: evaluation.evaluationId,
      tenderId: evaluation.tenderId,
      evaluationType: evaluation.evaluationType,
      prNumber: evaluation.prNumber
    };

    if (evaluation.criteria && evaluation.criteria.length > 0) {
      queryParams.criteria = encodeURIComponent(JSON.stringify(evaluation.criteria));
    }

    this._router.navigate(
      [`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.EVALUATION_SCORING}`],
      { queryParams: queryParams }
    );
  }

  navigateToAssignEvaluators(): void {
    this._router.navigate([`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.ASSIGN_EVALUATORS}`]);
  }

  assignEvaluation(evaluationId: string): void {
    const evaluation = this.evaluations.find(e => e.evaluationId === evaluationId);
    
    if (!evaluation) {
      console.error('Evaluation not found:', evaluationId);
      return;
    }
    
    const queryParams: any = {
      tenderId: evaluation.tenderId,
      evaluationType: evaluation.evaluationType
    };

    if (evaluation.bidId) {
      queryParams.vendorId = evaluation.bidId;
    }

    if (evaluation.selectedEvaluatorIds && evaluation.selectedEvaluatorIds.length > 0) {
      queryParams.selectedIds = evaluation.selectedEvaluatorIds.join(',');
    }

    this._router.navigate(
      [`/${AppConstants.ROUTES.EVALUATIONS}/${AppConstants.ROUTES.ASSIGN_EVALUATORS}`],
      { queryParams: queryParams }
    );
  }

  viewTender(tenderId: string): void {
    this._router.navigate([`/${AppConstants.ROUTES.TENDER}/${AppConstants.ROUTES.TENDERDETAILS}/${tenderId}`]);
  }

  viewBid(bidId: string): void {
    // TODO: Navigate to bid details page
    console.log('View Bid:', bidId);
  }

  publishMeeting(evaluationId: string): void {
    // TODO: Implement publish meeting functionality
    console.log('Publish Meeting:', evaluationId);
  }
}

